import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CreditCard,
  LogOut,
  MessageSquare,
  NotebookPen,
  Plus,
  TrendingUp,
  Upload,
  Video,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation, Trans } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NotificationBell } from "@/components/notification-bell";
import { useSubscription } from "@/hooks/useSubscription";
import { createPortalSession } from "@/lib/subscription.functions";
import logo from "@/assets/smart-word-labs-logo.png.asset.json";

export const Route = createFileRoute("/_authenticated/dashboard")({
  validateSearch: (search: Record<string, unknown>): { checkout?: "success" } =>
    search["checkout"] === "success" ? { checkout: "success" } : {},
  head: () => ({
    meta: [
      { title: "Parent Portal | Smart Word Labs" },
      { name: "robots", content: "noindex, nofollow" },
      {
        name: "description",
        content:
          "Manage lessons, billing, progress reports and your children's profiles in the Smart Word Labs parent portal.",
      },
      { property: "og:title", content: "Parent Portal | Smart Word Labs" },
      {
        property: "og:description",
        content: "Lessons, billing and progress for every child in one parent account.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: Dashboard,
});

function Dashboard() {
  const { t } = useTranslation("portal");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch();
  const [activeChild, setActiveChild] = useState<string | null>(null);
  const [addingChild, setAddingChild] = useState(false);
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  const openPortalFn = useServerFn(createPortalSession);

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth.user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, email, role, country, timezone")
        .eq("id", auth.user.id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });


  const {
    subscription,
    isActive,
    isPastDue,
    cancelsAtPeriodEnd,
    periodEnd,
    planLabel,
    environment,
    refetch: refetchSubscription,
  } = useSubscription(profile?.id);

  async function openPortal() {
    setPortalBusy(true);
    try {
      const result = await openPortalFn({ data: { environment } });
      if ("error" in result) throw new Error(result.error);
      if (!result.url) throw new Error(t("dashboard.toasts.portalUnavailable"));
      window.open(result.url, "_blank", "noopener");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("dashboard.toasts.portalError"));
    } finally {
      setPortalBusy(false);
    }
  }

  // The payment webhook can land a moment after the success redirect — poll briefly.
  useEffect(() => {
    if (search.checkout !== "success") return;
    toast.success(t("dashboard.toasts.paymentReceived.title"), {
      description: t("dashboard.toasts.paymentReceived.description"),
    });
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      refetchSubscription();
      if (tries >= 10) clearInterval(timer);
    }, 2000);
    navigate({ to: "/dashboard", replace: true, search: {} });
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.checkout]);

  const { data: children } = useQuery({
    queryKey: ["children", profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("id, full_name, age, cefr_level")
        .eq("parent_id", profile!.id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });


  useEffect(() => {
    if (!activeChild && children && children.length > 0) setActiveChild(children[0]!.id);
  }, [children, activeChild]);

  const { data: bookings } = useQuery({
    queryKey: ["bookings", profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select(
          "id, scheduled_at, status, payment_status, meeting_url, child_id, courses_and_cohorts(title)",
        )
        .eq("student_id", profile!.id)
        .order("scheduled_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const { data: homework } = useQuery({
    queryKey: ["homework", profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("homework_submissions")
        .select("id, status, tutor_feedback_text, created_at")
        .eq("student_id", profile!.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });


  const { data: messages } = useQuery({
    queryKey: ["tutor-messages", profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("id, title, message, created_at")
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data;
    },
  });

  const { data: isAdmin } = useQuery({
    queryKey: ["is-admin", profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: profile!.id,
        _role: "admin",
      });
      if (error) return false;
      return Boolean(data);
    },
  });

  const childBookings = (bookings ?? []).filter(
    (b) => !activeChild || !b.child_id || b.child_id === activeChild,
  );
  const upcoming = childBookings.find((b) => new Date(b.scheduled_at) >= new Date());
  const reviewedCount = (homework ?? []).filter((h) => h.status === "reviewed").length;

  async function addChild(e: React.FormEvent) {
    e.preventDefault();
    if (!profile?.id) return;
    const { error } = await supabase.from("children").insert({
      parent_id: profile.id,
      full_name: childName.trim(),
      age: childAge ? Number(childAge) : null,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    setChildName("");
    setChildAge("");
    setAddingChild(false);
    queryClient.invalidateQueries({ queryKey: ["children", profile.id] });
    toast.success(t("dashboard.toasts.childAdded"));
  }

  async function signOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="border-b border-border bg-background/85 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <span className="flex items-center gap-3 font-display text-xl text-primary">
            <img
              src={logo.url}
              alt={t("dashboard.logoAlt")}
              width={1920}
              height={1047}
              decoding="async"
              className="h-9 w-auto object-contain"
            />
            <span className="sr-only">{t("dashboard.logoAlt")}</span>
          </span>
          <div className="flex items-center gap-1">
            {isAdmin ? (
              <>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/admin">{t("dashboard.nav.adminPortal")}</Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/messages">
                    <MessageSquare /> {t("dashboard.nav.enquiries")}
                  </Link>
                </Button>
              </>
            ) : (
              <Button variant="ghost" size="sm" asChild>
                <Link to="/payment-help">
                  <MessageSquare /> Payment help
                </Link>
              </Button>
            )}
            <NotificationBell userId={profile?.id} />
            <Button variant="ghost" size="sm" onClick={signOut}>
              <LogOut /> {t("dashboard.nav.signOut")}
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 px-5 py-10">
        <section className="surface-card p-7">
          <Badge className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary">
            {isAdmin ? t("dashboard.hero.adminBadge") : t("dashboard.hero.parentBadge")}
          </Badge>

          <h1 className="mt-4 text-3xl text-primary">
            {t("dashboard.hero.greeting", {
              name: profile?.full_name ? `, ${profile.full_name.split(" ")[0]}` : "",
            })}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("dashboard.hero.subtitle")}
          </p>

          {/* Child sub-profiles */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {(children ?? []).map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveChild(c.id)}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                  activeChild === c.id
                    ? "border-accent bg-accent/15 text-primary"
                    : "border-border bg-card text-muted-foreground hover:border-accent/50 hover:text-primary"
                }`}
              >
                {c.full_name}
                {c.age ? ` · ${c.age}` : ""}
              </button>
            ))}
            <Button
              variant="softOutline"
              size="sm"
              className="rounded-full"
              onClick={() => setAddingChild((v) => !v)}
            >
              <Plus /> {t("dashboard.hero.addChild")}
            </Button>
          </div>

          {addingChild && (
            <form onSubmit={addChild} className="mt-4 grid gap-3 sm:grid-cols-[1fr_110px_auto]">
              <div className="space-y-1">
                <Label htmlFor="newChildName">{t("dashboard.hero.form.childName")}</Label>
                <Input
                  id="newChildName"
                  value={childName}
                  onChange={(e) => setChildName(e.target.value)}
                  maxLength={100}
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="newChildAge">{t("dashboard.hero.form.age")}</Label>
                <Input
                  id="newChildAge"
                  type="number"
                  min={3}
                  max={14}
                  value={childAge}
                  onChange={(e) => setChildAge(e.target.value)}
                />
              </div>
              <Button type="submit" variant="hero" className="self-end">
                {t("dashboard.hero.form.save")}
              </Button>
            </form>
          )}
        </section>

        {!isActive && (
          <section className="surface-card border-accent/50 p-7">
            <h2 className="text-xl text-primary">{t("dashboard.plan.title")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("dashboard.plan.description")}
            </p>
            <Button variant="hero" size="sm" className="mt-5" asChild>
              <a href="/#pricing">{t("dashboard.plan.viewPlans")}</a>
            </Button>
          </section>
        )}

        {/* Join class launcher */}

        <section className="gradient-ink rounded-2xl p-7 text-primary-foreground">
          <div className="flex flex-wrap items-center justify-between gap-5">
            <div>
              <h2 className="flex items-center gap-2 text-xl text-primary-foreground">
                <Video className="size-5 text-accent" /> {t("dashboard.studentMode.title")}
              </h2>
              <p className="mt-2 text-sm text-primary-foreground/75">
                {upcoming
                  ? t("dashboard.studentMode.nextLesson", {
                      datetime: new Date(upcoming.scheduled_at).toLocaleString(),
                    })
                  : t("dashboard.studentMode.noLesson")}
              </p>
            </div>
            {upcoming?.meeting_url ? (
              <Button variant="hero" size="xl" asChild>
                <a href={upcoming.meeting_url} target="_blank" rel="noreferrer">
                  {t("dashboard.studentMode.joinClass")}
                </a>
              </Button>
            ) : (
              <Button variant="hero" size="xl" disabled>
                {t("dashboard.studentMode.joinClass")}
              </Button>
            )}
          </div>
        </section>

        {/* Scheduling */}
        <section className="surface-card p-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 text-xl text-primary">
              <CalendarDays className="size-5 text-accent" /> {t("dashboard.scheduling.title")}
            </h2>
            <Button variant="softOutline" size="sm" asChild>
              <Link to="/book">{t("dashboard.scheduling.bookAnother")}</Link>
            </Button>
          </div>
          {childBookings.length > 0 ? (
            <ul className="mt-4 divide-y divide-border">
              {childBookings.map((b) => (
                <li
                  key={b.id}
                  className="flex flex-wrap items-center justify-between gap-4 py-3 transition-colors hover:bg-secondary/40"
                >
                  <div>
                    <p className="font-semibold text-primary">
                      {b.courses_and_cohorts?.title ?? t("dashboard.scheduling.lessonFallback")}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(b.scheduled_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{b.status}</Badge>
                    <Badge variant="outline">{b.payment_status}</Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        toast(t("dashboard.scheduling.rescheduleToast.title"), {
                          description: t("dashboard.scheduling.rescheduleToast.description"),
                        })
                      }
                    >
                      {t("dashboard.scheduling.reschedule")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-4 rounded-xl border border-dashed border-border bg-secondary/30 p-6 text-center">
              <p className="font-semibold text-primary">
                {t("dashboard.scheduling.empty.title")}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                {t("dashboard.scheduling.empty.description")}
              </p>
              <Button variant="hero" size="lg" className="mt-4" asChild>
                <Link to="/book">{t("dashboard.scheduling.empty.cta")}</Link>
              </Button>
            </div>
          )}

        </section>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Billing */}
          <section className="surface-card p-7">
            <h2 className="flex items-center gap-2 text-xl text-primary">
              <CreditCard className="size-5 text-accent" /> {t("dashboard.billing.title")}
            </h2>
            {isPastDue && (
              <p className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {t("dashboard.billing.pastDue")}
              </p>
            )}
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">{t("dashboard.billing.plan")}</dt>
                <dd className="font-semibold text-primary">
                  {isActive ? planLabel : t("dashboard.billing.noPlan")}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">{t("dashboard.billing.status")}</dt>
                <dd className="font-semibold text-primary capitalize">
                  {subscription?.status ?? t("dashboard.billing.statusNone")}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">
                  {cancelsAtPeriodEnd ? t("dashboard.billing.accessEnds") : t("dashboard.billing.renewsOn")}
                </dt>
                <dd className="font-semibold text-primary">
                  {periodEnd ? periodEnd.toLocaleDateString() : "—"}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">{t("dashboard.billing.childrenEnrolled")}</dt>
                <dd className="font-semibold text-primary">{children?.length ?? 0}</dd>
              </div>
            </dl>
            {isActive ? (
              <Button
                variant="softOutline"
                size="sm"
                className="mt-5"
                disabled={portalBusy}
                onClick={openPortal}
              >
                {portalBusy ? t("dashboard.billing.opening") : t("dashboard.billing.manageSubscription")}
              </Button>
            ) : (
              <Button variant="hero" size="sm" className="mt-5" asChild>
                <a href="/#pricing">{t("dashboard.billing.choosePlan")}</a>
              </Button>
            )}
          </section>

          {/* Progress */}
          <section className="surface-card p-7">
            <h2 className="flex items-center gap-2 text-xl text-primary">
              <TrendingUp className="size-5 text-accent" /> {t("dashboard.progress.title")}
            </h2>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">{t("dashboard.progress.cefrLevel")}</dt>
                <dd className="font-semibold text-primary">
                  {children?.find((c) => c.id === activeChild)?.cefr_level ?? t("dashboard.progress.awaitingAssessment")}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">{t("dashboard.progress.lessonsCompleted")}</dt>
                <dd className="font-semibold text-primary">
                  {childBookings.filter((b) => b.status === "completed").length}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">{t("dashboard.progress.homeworkReviewed")}</dt>
                <dd className="font-semibold text-primary">{reviewedCount}</dd>
              </div>
            </dl>
            {isActive ? (
              <Button variant="softOutline" size="sm" className="mt-5" asChild>
                <Link to="/homework">
                  <Upload /> {t("dashboard.progress.submitHomework")}
                </Link>
              </Button>
            ) : (
              <Button variant="softOutline" size="sm" className="mt-5" disabled>
                <Upload /> {t("dashboard.progress.submitHomework")}
              </Button>
            )}
          </section>
        </div>

        {/* Homework feedback */}
        <section className="surface-card p-7">
          <h2 className="flex items-center gap-2 text-xl text-primary">
            <NotebookPen className="size-5 text-accent" /> {t("dashboard.homeworkFeedback.title")}
          </h2>
          {homework && homework.length > 0 ? (
            <ul className="mt-4 divide-y divide-border">
              {homework.map((h) => (
                <li key={h.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {new Date(h.created_at).toLocaleDateString()}
                    </span>
                    <Badge variant="secondary">{h.status}</Badge>
                  </div>
                  {h.tutor_feedback_text && (
                    <p className="mt-2 text-sm text-primary">{h.tutor_feedback_text}</p>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">{t("dashboard.homeworkFeedback.empty")}</p>
          )}
        </section>

        {/* Messages */}
        <section className="surface-card p-7">
          <h2 className="flex items-center gap-2 text-xl text-primary">
            <MessageSquare className="size-5 text-accent" /> {t("dashboard.messages.title")}
          </h2>
          {messages && messages.length > 0 ? (
            <ul className="mt-4 divide-y divide-border">
              {messages.map((m) => (
                <li key={m.id} className="py-3">
                  <p className="text-sm font-semibold text-primary">{m.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{m.message}</p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">
              {t("dashboard.messages.empty")}
            </p>
          )}
        </section>
      </main>
    </div>
  );
}
